import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Users, Video, Search, MapPin, Globe, Calendar, User as UserIcon, Lock, Bell } from 'lucide-react';

const Groups = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [notifyMe, setNotifyMe] = useState(false);

  // Preview groups — sourced from live API once the meetups microservice launches.
  // Every group below is a real-world social-skills practice circle, NOT a clinical
  // or mental-health service. HiBFF is a practice gym, not a therapy provider.
  const allGroups = [
    {
      id: 1, name: 'Confidence Building Circle', category: 'Practice', members: 245,
      country: 'USA', city: 'New York', nextSession: '2026-03-22T18:00:00',
      facilitator: 'Sarah Miller', facilitatorCredentials: 'Certified HiBFF Coach',
      description: 'Practice everyday conversations in a safe, judgement-free circle.',
      meetingType: 'video', language: 'English'
    },
    {
      id: 2, name: 'Real-World Conversation Group', category: 'Practice', members: 189,
      country: 'UK', city: 'London', nextSession: '2026-03-23T19:00:00',
      facilitator: 'James Wilson', facilitatorCredentials: 'Communication Skills Coach',
      description: 'Rehearse the conversations that matter — friendships, jobs, family.',
      meetingType: 'video', language: 'English'
    },
    {
      id: 3, name: 'Mindful Moments', category: 'Wellbeing', members: 412,
      country: 'USA', city: 'San Francisco', nextSession: '2026-03-21T20:00:00',
      facilitator: 'Maya Patel', facilitatorCredentials: 'Certified Wellbeing Guide',
      description: 'Daily check-ins to reset, refocus, and stay grounded.',
      meetingType: 'video', language: 'English'
    },
    {
      id: 4, name: 'Brave Friends Meetup', category: 'Peer Practice', members: 156,
      country: 'Canada', city: 'Toronto', nextSession: '2026-03-24T17:00:00',
      facilitator: 'Robert Chen', facilitatorCredentials: 'Youth Mentor',
      description: 'Peer-to-peer practice for the social challenges that matter most.',
      meetingType: 'video', language: 'English'
    },
    {
      id: 5, name: 'Family Charter Conversations', category: 'Family', members: 98,
      country: 'USA', city: 'Los Angeles', nextSession: '2026-03-22T16:00:00',
      facilitator: 'Linda Martinez', facilitatorCredentials: 'Family Communication Coach',
      description: 'Workshops for parents and teens to practise honest, low-stakes talks.',
      meetingType: 'video', language: 'English'
    },
    {
      id: 6, name: 'Teens Building Confidence', category: 'Age 13-19', members: 334,
      country: 'USA', city: 'Chicago', nextSession: '2026-03-23T18:30:00',
      facilitator: 'Alex Johnson', facilitatorCredentials: 'Youth Programme Lead',
      description: 'Group practice sessions tuned for the realities of being a teen today.',
      meetingType: 'video', language: 'English'
    }
  ];

  const countries = ['all', ...new Set(allGroups.map(g => g.country))];
  const cities = selectedCountry === 'all'
    ? ['all']
    : ['all', ...new Set(allGroups.filter(g => g.country === selectedCountry).map(g => g.city))];

  const filteredGroups = allGroups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         group.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCountry = selectedCountry === 'all' || group.country === selectedCountry;
    const matchesCity = selectedCity === 'all' || group.city === selectedCity;
    return matchesSearch && matchesCountry && matchesCity;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="groups-page">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Practice Group Directory</h1>
        <p className="text-gray-600 mb-6">Join facilitated real-world practice circles with certified HiBFF coaches</p>

        {/* Coming Soon Banner */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="groups-coming-soon">
          <div className="flex items-start gap-3">
            <Lock size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Groups — Coming Soon</p>
              <p className="text-xs text-amber-700 mb-3">Live group sessions with Agora WebRTC will launch post-investment. Browse our planned directory below.</p>
              <button
                onClick={() => setNotifyMe(true)}
                disabled={notifyMe}
                className="text-xs font-semibold text-amber-800 underline disabled:no-underline disabled:text-amber-600"
                data-testid="groups-notify-btn"
              >
                {notifyMe ? '✓ We\u2019ll let you know' : 'Notify me when it launches'}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search groups..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="groups-search-input"
              />
            </div>
            <select
              value={selectedCountry}
              onChange={(e) => { setSelectedCountry(e.target.value); setSelectedCity('all'); }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="groups-country-select"
            >
              {countries.map(c => <option key={c} value={c}>{c === 'all' ? 'All countries' : c}</option>)}
            </select>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              disabled={selectedCountry === 'all'}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50 disabled:text-gray-400"
              data-testid="groups-city-select"
            >
              {cities.map(c => <option key={c} value={c}>{c === 'all' ? 'All cities' : c}</option>)}
            </select>
          </div>
        </div>

        {/* Groups Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredGroups.map(group => (
            <div key={group.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition" data-testid={`group-card-${group.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-gray-900">{group.name}</h3>
                  <span className="inline-block mt-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">{group.category}</span>
                </div>
                <Video size={18} className="text-gray-400" />
              </div>
              <p className="text-sm text-gray-600 mb-3">{group.description}</p>
              <div className="text-xs text-gray-500 space-y-1.5">
                <div className="flex items-center gap-1.5"><UserIcon size={12} /> {group.facilitator} · {group.facilitatorCredentials}</div>
                <div className="flex items-center gap-1.5"><Users size={12} /> {group.members} members</div>
                <div className="flex items-center gap-1.5"><MapPin size={12} /> {group.city}, {group.country}</div>
                <div className="flex items-center gap-1.5"><Calendar size={12} /> Next session: {formatDate(group.nextSession)}</div>
                <div className="flex items-center gap-1.5"><Globe size={12} /> {group.language}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Groups;
